import { useCallback, useRef, useState } from 'react';
import AnalyticsReportsApi from 'api/analytics-reports';
import type { ReportFormat, ReportLayout } from 'api/analytics-reports/types';
import { ApiClientError } from 'api/client';
import { isUnauthorizedApiClientError } from 'api/client/isUnauthorizedApiClientError';
import { useAnalyticsFiltersStore } from 'app/modules/analytics/store/analyticsFiltersStore';
import { useAnalyticsSelection } from 'app/modules/analytics/hooks/useAnalyticsSelection';
import type { QuickReportScope } from 'app/modules/analytics/modules/reports/constants/quickReportDefaults';
import { useActiveReportRuns } from 'app/modules/analytics/modules/reports/context/ActiveReportRunsContext';
import { deriveActiveReportProfiles } from 'app/modules/analytics/modules/reports/utils/deriveActiveReportProfiles';
import { buildOverviewQuickReportPayload } from 'app/modules/analytics/modules/reports/utils/buildOverviewQuickReportPayload';
import { buildQuickReportPayload } from 'app/modules/analytics/modules/reports/utils/buildQuickReportPayload';
import {
  buildQuickReportProfileCards,
  resolveDefaultQuickReportTemplate,
} from 'app/modules/analytics/modules/reports/utils/buildQuickReportProfileCards';
import { collectAvailableReportMetricsFromTemplates } from 'app/modules/analytics/modules/reports/utils/collectAvailableReportMetricsFromTemplates';
import { useReportTemplatesQuery } from 'app/modules/analytics/modules/reports/modules/createReport/hooks/useReportTemplatesQuery';
import { showAnalyticsReportGeneratingToast } from 'app/modules/analytics/modules/reports/utils/analyticsReportGeneratingToast';
import { useToast } from 'app/hooks/useToast';
import { useTranslation } from 'react-i18next';
import { useUser } from 'modules/user/userSelector';

export interface QuickAnalyticsReportExportParams {
  scope: QuickReportScope;
  format: ReportFormat;
  layout?: ReportLayout;
}

export function useQuickAnalyticsReportExport() {
  const { t } = useTranslation();
  const toast = useToast();
  const user = useUser();
  const { addActiveRun } = useActiveReportRuns();
  const templatesQuery = useReportTemplatesQuery();
  const { isGroupSelected, selectedPage, selectedProfileGroup } =
    useAnalyticsSelection();
  const startDate = useAnalyticsFiltersStore((state) => state.startDate);
  const endDate = useAnalyticsFiltersStore((state) => state.endDate);
  const datePreset = useAnalyticsFiltersStore((state) => state.datePreset);
  const isExportingRef = useRef(false);
  const [isExporting, setIsExporting] = useState(false);

  const exportQuickReport = useCallback(
    async ({ scope, format, layout }: QuickAnalyticsReportExportParams) => {
      if (isExportingRef.current) {
        return;
      }

      const hasProfileSelection = isGroupSelected
        ? selectedProfileGroup !== null
        : selectedPage !== null;

      if (!hasProfileSelection) {
        return;
      }

      const templates = templatesQuery.data ?? [];
      const template = resolveDefaultQuickReportTemplate(templates);

      if (!template) {
        toast(
          t('analyticsReports.createReport.templatesLoadError', {
            defaultValue: 'Could not load report templates.',
          }),
          'error',
        );
        return;
      }

      const profileLabel = isGroupSelected
        ? selectedProfileGroup?.name ?? t('analyticsReports.quickReportGroup')
        : selectedPage?.name ?? t('analyticsReports.quickReportProfile');

      const dateRangeLabel = `${startDate.format(
        'D MMM YYYY',
      )} – ${endDate.format('D MMM YYYY')}`;

      const { performance: allPerformanceMetrics } =
        collectAvailableReportMetricsFromTemplates(templates);

      // TODO(overview-pdf-be): použiť scope 'overview' + buildOverviewQuickReportProfileCards
      // namiesto 'full' / growth_pulse template cards. @see overviewPdfExportBackendGaps.ts
      const profileCards = buildQuickReportProfileCards(
        scope === 'overview' ? 'full' : scope,
        template,
        isGroupSelected,
        selectedProfileGroup,
        selectedPage,
        scope === 'performance' ? allPerformanceMetrics : [],
      );

      const reportName =
        scope === 'overview'
          ? t('analyticsReports.quickOverviewReportName', {
              profile: profileLabel,
              dateRange: dateRangeLabel,
              defaultValue: 'Overview report – {{profile}} – {{dateRange}}',
            })
          : t('analyticsReports.quickReportName', {
              profile: profileLabel,
              dateRange: dateRangeLabel,
            });

      const payload =
        scope === 'overview'
          ? // TODO(overview-pdf-be): overview payload (template overview + kind overview).
            // Teraz growth_pulse workaround kvôli 422 na create. @see overviewPdfExportBackendGaps.ts
            buildOverviewQuickReportPayload({
              name: reportName,
              datePreset,
              startDate,
              endDate,
              isGroupSelected,
              selectedProfileGroup,
              template,
              profileCards,
            })
          : buildQuickReportPayload({
              scope,
              name: reportName,
              profileCards,
              datePreset,
              startDate,
              endDate,
              isGroupSelected,
              selectedProfileGroup,
              template,
              templateCode: template.code,
            });

      isExportingRef.current = true;
      setIsExporting(true);

      try {
        const report = await AnalyticsReportsApi.createReport(payload);
        const run = await AnalyticsReportsApi.generateReport(report.id, {
          format,
          layout,
        });

        addActiveRun({
          reportId: report.id,
          runId: run.id,
          name: report.name,
          profiles: deriveActiveReportProfiles(
            selectedPage,
            selectedProfileGroup,
            isGroupSelected,
          ),
          createdBy: {
            name: user.name,
            avatarUrl: user.avatar?.src,
          },
          createdAt: new Date().toISOString(),
          format,
          generatingStatus: run.status === 'running' ? 'running' : 'pending',
          autoDownloadOnComplete: true,
        });
        showAnalyticsReportGeneratingToast(t, run.id);
      } catch (error) {
        if (isUnauthorizedApiClientError(error as ApiClientError)) {
          return;
        }

        const apiError = error as ApiClientError;
        toast(
          apiError.userMessage ??
            t('analyticsReports.generateError', {
              defaultValue: 'Could not generate report.',
            }),
          'error',
        );
      } finally {
        isExportingRef.current = false;
        setIsExporting(false);
      }
    },
    [
      addActiveRun,
      datePreset,
      endDate,
      isGroupSelected,
      selectedPage,
      selectedProfileGroup,
      startDate,
      t,
      templatesQuery.data,
      toast,
      user.avatar?.src,
      user.name,
    ],
  );

  return {
    exportQuickReport,
    isExporting,
    isTemplatesLoading: templatesQuery.isLoading,
  };
}
